import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api, setAccessToken, getAccessToken, refreshAccessToken, setActiveWorkspaceId as setWorkspaceHeader } from '../api/client';
import { applyLanguage } from '../i18n';

// ── Plan helpers ─────────────────────────────────────────────────────────────
const PRO_PLANS = ['pro', 'team', 'enterprise'];
const TEAM_PLANS = ['team', 'enterprise'];

/** True when the user is on Pro or above, OR is in an active trial. */
export function isPro(user) {
  if (!user) return false;
  if (user.isTrialing || user.subscriptionStatus === 'trialing') return true;
  return PRO_PLANS.includes((user.plan || '').toLowerCase());
}

/** True when the user is on Team Pro or Enterprise, OR is in an active trial. */
export function isTeam(user) {
  if (!user) return false;
  if (user.isTrialing || user.subscriptionStatus === 'trialing') return true;
  return TEAM_PLANS.includes((user.plan || '').toLowerCase());
}

// ✅ CREATE CONTEXT
const CurrentUserContext = createContext(null);

// ✅ HOOK TO USE CONTEXT
export const useCurrentUser = () => {
  const context = useContext(CurrentUserContext);
  if (!context) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  }
  return context;
};

// ✅ CONTEXT PROVIDER COMPONENT
export const CurrentUserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [authConnectionFailed, setAuthConnectionFailed] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  const [workspacesLoadError, setWorkspacesLoadError] = useState(false);
  const [subscriptionCheckPending, setSubscriptionCheckPending] = useState(false);
  const workspacesRef = useRef([]);

  // ✅ AUTHENTICATION METHODS
  const login = useCallback(async (email, password, trusted = false) => {
    try {
      setLoading(true);
      setError(null);

      const res = await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, trusted }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Invalid email or password');
      }

      if (trusted) {
        localStorage.setItem('aidey_trusted_device', 'true');
      } else {
        localStorage.removeItem('aidey_trusted_device');
      }

      setAccessToken(data.accessToken);
      setCurrentUser(data.user);
      applyLanguage(data.user?.language);
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz && tz !== data.user?.timezone) {
          api('/api/users/me/timezone', { method: 'PATCH', body: JSON.stringify({ timezone: tz }) }).catch(() => {});
        }
      } catch (_) {}
      await loadNotifications();
      await loadWorkspaces();

      // If the user arrived via a notification deep-link while logged out, a
      // returnTo path was parked in sessionStorage. Dispatch aidey:navigate so
      // AppContent routes them there now that auth is confirmed.
      try {
        const returnTo = sessionStorage.getItem('aidey_return_to');
        if (returnTo) {
          sessionStorage.removeItem('aidey_return_to');
          window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: returnTo }));
        }
      } catch {}

      try {
        const notifCtx = sessionStorage.getItem('aidey_notif_ctx');
        if (notifCtx) {
          sessionStorage.removeItem('aidey_notif_ctx');
          const ctx = JSON.parse(notifCtx);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('aidey:open-chat', { detail: { notifContext: ctx } }));
          }, 600);
        }
      } catch {}

      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch (_) {
      // best-effort
    }
    setAccessToken(null);
    setCurrentUser(null);
    setNotifications([]);
    setUnreadCount(0);
    setError(null);
    localStorage.removeItem('currentUserId');
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('currentUser');
  }, []);

  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const res = await api('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      const data = await res.json();

      // Verify-email-first: registration no longer logs the user in. The server
      // returns a neutral { ok: true } for both new and existing emails; the user
      // activates via the emailed link, then signs in. No token to store here.
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ USER PROFILE METHODS
  const updateProfile = useCallback(async (updates) => {
    if (!currentUser) return;

    try {
      const res = await api('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }

      setCurrentUser(data.user);
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [currentUser]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    if (!currentUser) return;

    try {
      const res = await api('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to change password');
      }
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [currentUser]);

  // ✅ NOTIFICATION METHODS
  const loadNotifications = useCallback(async () => {
    if (!getAccessToken()) return; // no token — skip, avoids 401 before session is restored
    try {
      const res = await api('/api/notifications');
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Error loading notifications:', err);
    }
  }, []);

  // Notifications are now workspace-scoped server-side (by the X-Active-Workspace
  // header), so refetch when the active workspace changes — otherwise the bell/
  // dashboard would show the previous workspace's set until the next reload.
  useEffect(() => {
    if (activeWorkspaceId) loadNotifications();
  }, [activeWorkspaceId, loadNotifications]);

  const switchWorkspace = useCallback((id) => {
    setActiveWorkspaceId(id);
    setWorkspaceHeader(id);
    try { localStorage.setItem('aidey_active_workspace', id); } catch (_) {}
  }, []);

  const loadWorkspaces = useCallback(async () => {
    if (!getAccessToken()) return;
    const tryFetch = async () => {
      const res = await api('/api/workspaces');
      return res.json();
    };
    let list;
    try {
      list = await tryFetch();
    } catch {
      try {
        list = await tryFetch();
      } catch (err) {
        console.error('[workspaces] failed to load after retry:', err);
        setWorkspacesLoadError(true);
        const fallback = workspacesRef.current.find(w => w.type === 'business') || workspacesRef.current[0];
        if (fallback) {
          setActiveWorkspaceId(fallback.id);
          setWorkspaceHeader(fallback.id);
        }
        return;
      }
    }
    setWorkspacesLoadError(false);
    setWorkspaces(list);
    workspacesRef.current = list;

    // Restore the last active workspace if it is still in the fetched list.
    // Priority: saved preference → owned business → member workspace → list[0].
    // Member workspaces are flagged isMember:true by the backend; suspended members
    // are excluded from the list at the API level, so no guard needed here.
    let restoredId = null;
    try {
      const saved = localStorage.getItem('aidey_active_workspace');
      if (saved && list.find(w => w.id === saved)) restoredId = saved;
    } catch (_) {}

    const restored  = restoredId ? list.find(w => w.id === restoredId) : null;
    const biz       = list.find(w => w.type === 'business' && !w.isMember);
    const memberWs  = list.find(w => w.isMember);
    const target    = restored || biz || memberWs || list[0];
    if (target) {
      setActiveWorkspaceId(target.id);
      setWorkspaceHeader(target.id);
    }
  }, []);

  const markNotificationAsRead = useCallback(async (notificationId) => {
    if (!currentUser) return;

    try {
      await api(`/api/notifications/${notificationId}/read`, { method: 'PUT' });
      await loadNotifications();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, [currentUser, loadNotifications]);

  const markAllNotificationsAsRead = useCallback(async () => {
    if (!currentUser) return;
    try {
      await api('/api/notifications/read-all', { method: 'PUT' });
      await loadNotifications();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [currentUser, loadNotifications]);

  const dismissNotification = useCallback(async (id) => {
    try {
      await api(`/api/notifications/${id}/dismiss`, { method: 'PUT' });
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  }, []);

  const dismissNotifications = useCallback(async (ids) => {
    if (!ids?.length) return;
    try {
      await api('/api/notifications/dismiss-batch', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      });
      const idSet = new Set(ids);
      setNotifications(prev => prev.filter(n => !idSet.has(n.id)));
      setUnreadCount(prev => Math.max(0, prev - ids.length));
    } catch (err) {
      console.error('Error dismissing notifications:', err);
    }
  }, []);

  // @mention notification. The old path wrote to an in-memory mock and never
  // reached the backend, so mentions produce no real notification today. Kept as
  // a no-op to preserve that contract (TaskForm still calls it).
  // TODO: real mention notifications via POST /api/notifications.
  const notifyUserMentioned = useCallback(() => {}, []);

  // ✅ SESSION RESTORATION
  const restoreSession = useCallback(async () => {
    try {
      setLoading(true);
      setAuthConnectionFailed(false);

      // refreshAccessToken() participates in the shared refreshPromise so any
      // concurrent api() 401-retry waits on the same fetch rather than firing
      // a second rotation request that would invalidate the first.
      const data = await refreshAccessToken();

      if (data === 'network-error') {
        // A fetch failure (DNS, timeout, network down) — leave currentUser untouched
        // so an in-memory session (e.g. bfcache restore) survives, and show a
        // retry card instead of the login page.
        setAuthConnectionFailed(true);
        return;
      }

      if (data && data.user) {
        // New backend response includes user — no second round-trip needed.
        setCurrentUser(data.user);
        applyLanguage(data.user?.language);
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz && tz !== data.user?.timezone) {
            api('/api/users/me/timezone', { method: 'PATCH', body: JSON.stringify({ timezone: tz }) }).catch(() => {});
          }
        } catch (_) {}
        await loadNotifications();
        await loadWorkspaces();
      } else if (data && data.accessToken) {
        // Older backend compat: token refreshed but no user in response — fetch separately.
        const meRes = await api('/api/auth/me');
        if (meRes && meRes.ok) {
          const meData = await meRes.json();
          setCurrentUser(meData.user);
          applyLanguage(meData.user?.language);
          try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tz && tz !== meData.user?.timezone) {
              api('/api/users/me/timezone', { method: 'PATCH', body: JSON.stringify({ timezone: tz }) }).catch(() => {});
            }
          } catch (_) {}
          await loadNotifications();
          await loadWorkspaces();
        }
      }
      // data === false → refresh token invalid/missing → stay logged out
    } catch (err) {
      console.error('Error restoring session:', err);
    } finally {
      setLoading(false);
    }
  }, [loadNotifications]);

  const retryConnection = useCallback(() => {
    restoreSession();
  }, [restoreSession]);

  const updateCurrentUser = useCallback((updates) => {
    return updateProfile(updates);
  }, [updateProfile]);

  // ✅ PERMISSIONS
  const hasPermission = useCallback((permission) => {
    if (!currentUser) return false;

    const permissions = {
      admin: ['admin', 'super_admin'].includes(currentUser.role),
      super_admin: currentUser.role === 'super_admin',
      create_team: ['member', 'admin', 'super_admin'].includes(currentUser.role),
      manage_billing: ['admin', 'super_admin'].includes(currentUser.role),
      view_analytics: ['admin', 'super_admin'].includes(currentUser.role)
    };

    return permissions[permission] || false;
  }, [currentUser]);

  // ✅ EFFECT TO RESTORE SESSION ON MOUNT
  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // ✅ CLEAR AUTH WHEN API CLIENT SIGNALS SESSION IS DEAD
  useEffect(() => {
    const handleAuthExpired = () => {
      setAccessToken(null);
      setCurrentUser(null);
      setNotifications([]);
      setUnreadCount(0);
      localStorage.removeItem('currentUserId');
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('currentUser');
    };
    window.addEventListener('aidey:auth:expired', handleAuthExpired);
    return () => window.removeEventListener('aidey:auth:expired', handleAuthExpired);
  }, []);

  useEffect(() => {
    let retryTimer1 = null;
    let retryTimer2 = null;

    async function checkPersonalWorkspace() {
      const token = getAccessToken();
      if (!token) return false;
      try {
        const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const res = await fetch(`${BASE}/api/notes`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) return true;
        const body = await res.json().catch(() => ({}));
        return body?.code !== 'PERSONAL_SUBSCRIPTION_REQUIRED';
      } catch {
        return false;
      }
    }

    const handleSubscriptionRequired = () => {
      if (typeof window === 'undefined' || window.location.pathname === '/plan-select') return;
      setSubscriptionCheckPending(true);

      retryTimer1 = setTimeout(async () => {
        const ok = await checkPersonalWorkspace();
        if (ok) { setSubscriptionCheckPending(false); return; }

        retryTimer2 = setTimeout(async () => {
          const ok2 = await checkPersonalWorkspace();
          setSubscriptionCheckPending(false);
          if (!ok2) window.location.href = '/plan-select';
        }, 7000);
      }, 3000);
    };

    window.addEventListener('aidey:subscription:required', handleSubscriptionRequired);
    return () => {
      window.removeEventListener('aidey:subscription:required', handleSubscriptionRequired);
      clearTimeout(retryTimer1);
      clearTimeout(retryTimer2);
    };
  }, []);

  // ACCOUNT-4 — access enforcement. When the API 402s a blocked user, pull a
  // fresh currentUser (via the exempt /refresh path) so its enforcing verdict
  // (trialExpired/blockReason) is current and App renders the paywall. Guarded
  // so a burst of 402s from many in-flight calls triggers at most one refresh.
  useEffect(() => {
    let refreshing = false;
    const handleAccessBlocked = async () => {
      if (refreshing) return;
      if (currentUser?.trialExpired) return; // already reflecting the block
      refreshing = true;
      try { await restoreSession(); } finally { refreshing = false; }
    };
    window.addEventListener('aidey:access:blocked', handleAccessBlocked);
    return () => window.removeEventListener('aidey:access:blocked', handleAccessBlocked);
  }, [currentUser, restoreSession]);

  // ✅ WINDOW FOCUS HEALTH CHECK — proactively refresh if token is close to expiry.
  // Uses the shared refreshAccessToken() from the api client so it participates in
  // the refreshPromise deduplication — prevents a race where a background poll and
  // this handler both try to rotate the refresh token simultaneously, causing one
  // to receive a 401 from an already-consumed token and incorrectly firing logout.
  useEffect(() => {
    if (!currentUser) return;
    const handleFocus = async () => {
      const token = getAccessToken();
      if (!token) return;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000;
        if (expiresAt - Date.now() < 5 * 60 * 1000) {
          // Token expires in less than 5 minutes — refresh proactively.
          // refreshAccessToken() is safe to call concurrently: if a refresh is
          // already in flight it returns the same promise rather than a new fetch.
          await refreshAccessToken();
        }
      } catch (_) {}
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [currentUser]);

  // ✅ EFFECT TO POLL FOR NEW NOTIFICATIONS
  useEffect(() => {
    if (!currentUser) return;

    const pollInterval = setInterval(() => {
      loadNotifications();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [currentUser, loadNotifications]);

  // ✅ CONTEXT VALUE
  const value = {
    // User state
    currentUser,
    loading,
    error,
    notifications,
    unreadCount,

    // Authentication
    login,
    logout,
    register,

    // Profile management
    updateProfile,
    changePassword,

    // Notifications
    markNotificationAsRead,
    markAllNotificationsAsRead,
    loadNotifications,
    dismissNotification,
    dismissNotifications,

    // @Mentions (suggestion/parsing moved to useWorkspaceRoster)
    notifyUserMentioned,

    // Permissions
    hasPermission,

    // Workspace
    workspaces,
    activeWorkspaceId,
    switchWorkspace,
    loadWorkspaces,
    workspacesLoadError,

    // Utilities
    restoreSession,
    authConnectionFailed,
    retryConnection,
    subscriptionCheckPending,

    // Legacy compatibility
    setCurrentUser,
    updateCurrentUser,
    isAdmin:
      currentUser?.role === 'admin'
      || currentUser?.role === 'super_admin'
      || ((currentUser?.plan === 'Team' || currentUser?.plan === 'Enterprise') && !!currentUser?.isTeamOwner),

    // True for global admins, team admins, AND solo owners (no team) so a
    // one-person business can reach its own billing/financials. Only a non-admin
    // INVITED team member (has a teamId but teamRole 'user') gets false.
    isTeamAdmin:
      currentUser?.role === 'admin'
      || currentUser?.role === 'super_admin'
      || currentUser?.teamRole === 'admin'
      || (!!currentUser && !currentUser?.teamId),

    // Plan helpers (use these instead of checking plan strings directly)
    isPro: isPro(currentUser),
    isTeam: isTeam(currentUser),
  };

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};

export { CurrentUserContext };
export default CurrentUserProvider;
